import { Modal } from "react-bootstrap";
import React, { ReactNode, useCallback, useRef } from "react";
import { Circle, FeatureGroup, LayerGroup, MapContainer, Polyline, WMSTileLayer } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { DrawEvents, LatLng } from "leaflet";
import { Polygon } from "react-leaflet";
import Resources from "../../../../types/Resources";
import PositionPickerRow, { PositionPickerRow_default } from "../../../../types/forms/Permits/PositionPickerRow";
import MapDeleteButton from "./MapDeleteButton";

type DrawPolygonModalProps = {
    show: boolean;
    onClose: () => void;
    position: PositionPickerRow | undefined;
    onPositionUpdate: (updatedPosition: PositionPickerRow) => void;
    resources: Resources;
};

function DrawPolygonModal({ show, onClose, position, onPositionUpdate, resources }: DrawPolygonModalProps) {
    const areaFeatureGroupRef = useRef<any>(null);

    function createdHandler(e: DrawEvents.Created) {
        if (!areaFeatureGroupRef.current) {
            console.error("areaFeatureGroupRef is not set");
            return;
        }

        const layer = e.layer as any;
        areaFeatureGroupRef.current.clearLayers();

        const geoJson = layer.toGeoJSON();

        const type = geoJson.geometry.type;
        let latlngs: [[number, number]] | [number, number];
        let radius: number | undefined = undefined;

        if (type === "Polygon") {
            latlngs = layer._latlngs[0].map((p: LatLng) => [p.lat, p.lng]);
        } else if (type === "LineString") {
            latlngs = layer._latlngs.map((p: LatLng) => [p.lat, p.lng]);
        } else {
            latlngs = [layer._latlng.lat, layer._latlng.lng];
            radius = layer._mRadius;
        }

        const drawnPolygon: any = {
            latlngs: latlngs,
            options: layer.options,
            type: geoJson.geometry.type
        };

        if (radius) {
            drawnPolygon.radius = radius;
        }

        const updatedPosition: PositionPickerRow = !!position
            ? { ...position, polygon: drawnPolygon }
            : PositionPickerRow_default();
        onPositionUpdate(updatedPosition);
    }

    const handleDeletePolygon = useCallback(() => {
        const updatedPosition = !!position ? { ...position, polygon: undefined } : PositionPickerRow_default();
        onPositionUpdate(updatedPosition);
    }, [position, onPositionUpdate]);

    if (!position) return null;

    const polygonLayer: ReactNode = polygonToLeafletLayer(position.polygon);

    return (
        <Modal show={show} onHide={onClose} size="xl">
            <Modal.Header closeButton={true}>
                <Modal.Title>{resources.get("DrawArea")}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="rounded-bottom p-0 overflow-hidden">
                <div className="container-fluid p-0">
                    <div className="col-12">
                        <MapContainer
                            center={[57.688111, 11.848859]}
                            zoom={14}
                            style={{ height: "80vh" }}
                            attributionControl={false}
                        >
                            <FeatureGroup ref={areaFeatureGroupRef}>
                                <EditControl
                                    position="topright"
                                    edit={{ remove: false, edit: false }}
                                    onCreated={createdHandler}
                                    draw={{
                                        polyline: {
                                            shapeOptions: {
                                                color: "#f357a1",
                                                weight: 3
                                            }
                                        },
                                        polygon: {
                                            shapeOptions: {
                                                weight: 2,
                                                color: "#bada55"
                                            }
                                        },
                                        circle: true,
                                        rectangle: {
                                            shapeOptions: {
                                                weight: 2
                                            }
                                        },
                                        marker: false,
                                        circlemarker: false
                                    }}
                                />
                                <MapDeleteButton onClick={handleDeletePolygon} show={show} />
                            </FeatureGroup>
                            <WMSTileLayer
                                url="https://opengeodata.goteborg.se/services/ortofoto/wms/v1"
                                layers="orto_2023"
                                maxNativeZoom={19}
                                maxZoom={19}
                                minZoom={13}
                            />
                            <LayerGroup>{polygonLayer}</LayerGroup>
                        </MapContainer>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

function polygonToLeafletLayer(area: any): ReactNode {
    if (!area) return null;

    if (area.type === "Polygon") {
        return <Polygon positions={area.latlngs as [[number, number]]} pathOptions={area.options} />;
    }
    if (area.type === "LineString") {
        return <Polyline positions={area.latlngs as [[number, number]]} pathOptions={area.options} />;
    }

    return <Circle center={area.latlngs as [number, number]} pathOptions={area.options} radius={area.radius} />;
}

export default DrawPolygonModal;
